import React, { useState, useEffect } from 'react';
import './Home.css';
import { FaInstagram, FaFacebook, FaWhatsapp } from 'react-icons/fa';
import { TypeAnimation } from 'react-type-animation';
import visioLogo from '../../assets/images/visio-logo.png';
import useClickSound from '../../hooks/useClickSound';

const MESSAGES = [
  { id: 1, type: 'incoming', text: "Hi! I'd like to book a demo 👋", delay: 0 },
  { id: 2, type: 'outgoing', text: "Hey! I'm Visio 🤖 I'd love to show you what I can do. What's your availability this week?", delay: 1200 },
  { id: 3, type: 'incoming', text: 'Thursday works — morning?', delay: 2600 },
  { id: 4, type: 'outgoing', text: "Perfect! I've booked you in for Thursday at 10am. You'll get a confirmation shortly ✅", delay: 3800 },
  { id: 5, type: 'incoming', text: 'That was fast! 🔥', delay: 5200 },
  { id: 6, type: 'outgoing', text: 'Always. Your customers will never wait again 😊', delay: 6400 },
];

const CYCLE_DURATION = 9000;

const Home = () => {
  const playClickSound = useClickSound();
  const [visibleMessages, setVisibleMessages] = useState([]);
  const [showBadge, setShowBadge] = useState(false);

  useEffect(() => {
    let timeouts = [];

    const runCycle = () => {
      setVisibleMessages([]);
      setShowBadge(false);

      MESSAGES.forEach((msg) => {
        const t = setTimeout(() => {
          setVisibleMessages(prev => [...prev, msg.id]);
          if (msg.id === 2) setShowBadge(true);
        }, msg.delay);
        timeouts.push(t);
      });

      const restart = setTimeout(() => {
        runCycle();
      }, CYCLE_DURATION);
      timeouts.push(restart);
    };

    runCycle();
    return () => timeouts.forEach(clearTimeout);
  }, []);

  return (
    <section id="home" className="home-section">
      <div className="home-grid-bg" />

      <div className="container">
        <div className="home-content">

          {/* Left */}
          <div className="home-text">
            <div className="verified-badge">
              <span className="verified-dot" />
              Meta-Verified Tech Provider
            </div>

            <h1 className="home-headline">
              AI Agents That Work<br />
              <span className="highlight">While You Sleep.</span>
            </h1>

            <TypeAnimation
              sequence={[
                'WhatsApp AI that replies instantly.', 900,
                'Lead scoring on autopilot.', 900,
                'Calendar bookings — zero effort.', 900,
                'Customer support, 24/7.', 900,
                'Built for South African business.', 900,
              ]}
              wrapper="p"
              cursor={true}
              repeat={Infinity}
              speed={60}
              className="typing-animation"
            />

            <p className="description">
              Summit Visio builds AI-powered WhatsApp agents for businesses.
              Your customers get instant answers. You get qualified leads, booked appointments,
              and a business that never goes offline.
            </p>

            <div className="stats-row">
              <div className="stat">
                <span className="stat-number">&lt; 2s</span>
                <span className="stat-label">Response time</span>
              </div>
              <div className="stat-divider" />
              <div className="stat">
                <span className="stat-number">24/7</span>
                <span className="stat-label">Always online</span>
              </div>
              <div className="stat-divider" />
              <div className="stat">
                <span className="stat-number">100%</span>
                <span className="stat-label">WhatsApp native</span>
              </div>
            </div>

            <div className="cta-row">
              <a
                href="https://summitvisio.com/onboard"
                className="btn-primary card-effect"
                onClick={playClickSound}
              >
                <FaWhatsapp style={{ marginRight: 8 }} />
                Connect WhatsApp
              </a>
              <button className="btn-secondary card-effect" onClick={playClickSound}>
                Book A Demo
              </button>
            </div>

            <div className="social-links">
              <span className="find-with-me">Follow us</span>
              <div className="social-icons">
                <a href="https://www.instagram.com/summitvisio/" target="_blank" rel="noopener noreferrer" className="card-effect" onClick={playClickSound}><FaInstagram /></a>
                <a href="https://www.facebook.com/profile.php?id=61580704949082" target="_blank" rel="noopener noreferrer" className="card-effect" onClick={playClickSound}><FaFacebook /></a>
              </div>
            </div>
          </div>

          {/* Right — Animated Chat */}
          <div className="home-image">
            <div className="phone-mockup card-effect">

              <div className="chat-header">
                <div className="chat-avatar">
                  <img src={visioLogo} alt="Visio" className="chat-avatar-img" />
                </div>
                <div>
                  <p className="chat-name">Visio AI Agent</p>
                  <p className="chat-status">● Online</p>
                </div>
              </div>

              <div className="chat-body">
                {MESSAGES.map((msg) => (
                  <div
                    key={msg.id}
                    className={`chat-bubble ${msg.type} ${visibleMessages.includes(msg.id) ? 'visible' : ''}`}
                  >
                    {msg.text}
                  </div>
                ))}
              </div>

              <div className="chat-footer">
                <span className="chat-input-mock">Type a message...</span>
              </div>
            </div>

            <div className={`float-badge ${showBadge ? 'float-badge--visible' : ''}`}>
              <span className="float-badge__icon">⚡</span>
              <div>
                <p className="float-badge__title">New lead qualified</p>
                <p className="float-badge__sub">2 seconds ago · Hot 🔥</p>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default Home;